"use client";

import { useTranslations } from "next-intl";
import React, {
  ChangeEvent,
  ComponentPropsWithRef,
  FocusEvent,
  MouseEventHandler,
  ReactNode,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Label from "@/components/ui/Form/Label/Label";
import Icon from "@/components/ui/Icon/Icon";
import cn from "@/utils/cn";

export interface InputProps extends ComponentPropsWithRef<"input"> {
  name: string;
  label?: string;
  children?: ReactNode;
  note?: string;
  cta?: boolean;
  ctaLabel?: string;
  ctaHandler?: MouseEventHandler;
  search?: boolean;
  hideBorder?: boolean;
  standardHeight?: boolean;
  error?: boolean;
  errorMessage?: string;
  reverse?: boolean;
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  onClearField?: () => void;
  inputValue?: string;
  wrapperClassNames?: string;
}

export const InputBase = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      name,
      note,
      children,
      label,
      error,
      errorMessage,
      cta = false,
      search = false,
      ctaLabel,
      ctaHandler,
      reverse,
      hideBorder = false,
      standardHeight = true, //TODO da capire con fra
      field,
      fieldState,
      onClearField,
      wrapperClassNames,
      ...rest
    },
    ref,
  ) => {
    // const { field, fieldState } = useController({ control, name });
    const t = useTranslations();
    const [isError, setIsError] = useState<boolean>(error!);
    const defaultID = useId();
    const [isFocused, setIsFocuses] = useState<boolean>(false);
    const [displayError, setDisplayError] = useState<string>(errorMessage || "");
    const fieldType: string = useMemo(() => {
      return rest?.type || "text";
    }, [rest?.type]);

    const inputID: string = useMemo(() => {
      return rest?.id || defaultID;
    }, [rest?.id, defaultID]);

    const inputClasses: string = useMemo(() => {
      return cn("font-secondary appearance-none focus:outline-none w-full text-m bg-transparent rounded-none leading-[11px]", rest?.className);
    }, [rest?.className]);

    useEffect(() => {
      setIsError(!!fieldState ? !!fieldState?.error : error!);
    }, [error, fieldState]);

    useEffect(() => {
      if (fieldState && fieldState.error) {
        setDisplayError(t(fieldState.error.message) || "");
      }
    }, [fieldState, t]);

    return (
      <div className={rest.hidden ? "hidden" : wrapperClassNames ?? undefined}>
        <div className="block h-m">
          {label && (
            <Label htmlFor={defaultID} reverse={reverse} required={rest.required}>
              {label}
            </Label>
          )}
        </div>
        <div
          className={`flex items-center py-xs transition-all w-full ${!hideBorder ? `${isError || error ? "border-b border-[#B42318] text-[#B42318]" : "border-b border-gray-10 text-black"}` : ` `} ${isFocused && !hideBorder ? "  ring ring-inherit" : ""}`}
        >
          {search && (
            <span className="mr-xs">
              <Icon name="search" size="20" title="search" className="stroke-gray-50" />
            </span>
          )}

          <input
            {...rest}
            type={fieldType}
            {...field}
            ref={field?.ref ?? ref}
            name={name}
            id={inputID}
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
              setIsFocuses(true);
              rest?.onFocus?.(event);
            }}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setIsFocuses(false);
              rest?.onBlur?.(event);
              field?.onBlur();
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              rest?.onChange?.(event);
              field?.onChange(event);
              setIsError(error!);
            }}
            className={`${inputClasses} ${reverse ? "text-white" : ""}`}
            value={field?.value || rest?.value || ""}
          />

          {rest?.disabled && (
            <>
              {!rest?.className?.includes("noicon") && (
                <div className="flex items-end bg-transparent pl-xs">
                  <Icon name="uneditable" size="20" title="Uneditable" className="stroke-gray-50" />
                </div>
              )}
            </>
          )}
          {isError && (
            <>
              {!rest?.className?.includes("noicon") && (
                <div
                  className={`flex items-end bg-transparent px-xs h-[19px] cursor-pointer ${name === "password" && (error || isError) && "pr-xl"}`}
                  onClick={() => (onClearField ? onClearField() : field?.onChange(""))}
                >
                  <Icon name="fail" size="20" title="error" className="stroke-[#B42318]" />
                </div>
              )}
            </>
          )}
          {cta && (
            <Button onClick={ctaHandler} type="button" size={ButtonSize.SMALL} variant={ButtonVariant.PRIMARY} theme={ButtonTheme.BLACK}>
              {ctaLabel}
            </Button>
          )}
        </div>
        <span
          className={`block h-m text-xs leading-[7px] font-secondary ${isError || error ? "text-[#B42318]" : reverse ? "text-gray-10" : "text-gray-70"}  pt-xs px-xxs`}
        >
          {isError || error ? displayError || errorMessage : note ? note : ""}
        </span>
      </div>
    );
  },
);
InputBase.displayName = "InputBase";
export default InputBase;
